import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, CircularProgress, Chip,
  Button, IconButton, Tooltip, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Select, MenuItem, FormControl, InputLabel, Collapse,
  TablePagination, TableSortLabel, TextField, InputAdornment, Checkbox,
} from '@mui/material';
import {
  KeyboardArrowDown, KeyboardArrowRight, Refresh, Link as LinkIcon,
  LinkOff as LinkOffIcon, Sync as SyncIcon, Search as SearchIcon,
} from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import { parseOptionSymbol } from '../utils/tradeUtils';
import { buildOCCSymbol } from '../utils/optionSymbol';

const fmt$ = v => (v == null ? '—' : `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = iso => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const plColor = v => (v > 0 ? 'success.main' : v < 0 ? 'error.main' : 'text.secondary');

const Chains = () => {
  const [chains, setChains] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [linkHop, setLinkHop] = useState(null);       // {trade_ids, fromChainId, label}
  const [linkTarget, setLinkTarget] = useState('');
  const [selected, setSelected] = useState(new Set()); // chain ids checked for merge
  const [merging, setMerging] = useState(false);
  const [expandedHops, setExpandedHops] = useState(new Set()); // 'chainId:hop' keys
  const [statusFilter, setStatusFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPL, setTotalPL] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [duration, setDuration] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // Live marks for open hop contracts, keyed by whitespace-stripped symbol.
  const [hopQuotes, setHopQuotes] = useState({});
  const pendingQuotes = React.useRef(new Set());

  // Resolve the duration control into a from/to pair on the chain's Opened
  // date. Presets end today; 'custom' uses the two date inputs.
  const range = (() => {
    if (duration === 'custom') return { from: customFrom, to: customTo };
    if (duration === 'ytd') return { from: `${new Date().getFullYear()}-01-01`, to: '' };
    if (!duration) return { from: '', to: '' };
    const d = new Date();
    d.setDate(d.getDate() - parseInt(duration, 10));
    return { from: d.toISOString().slice(0, 10), to: '' };
  })();

  // Debounce the underlying search box so typing doesn't fire a query per key
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const accountName = id => accounts.find(a => String(a.id) === String(id))?.name || `#${id}`;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: rowsPerPage, offset: page * rowsPerPage });
      if (statusFilter) params.set('status', statusFilter);
      if (accountFilter) params.set('account_id', accountFilter);
      if (search) params.set('q', search);
      if (range.from) params.set('from', range.from);
      if (range.to) params.set('to', range.to);
      if (sortBy) { params.set('sort', sortBy); params.set('dir', sortDir); }
      const [aR, cR] = await Promise.all([
        apiRequest('/api/accounts'),
        apiRequest(`/api/trade-chains?${params}`),
      ]);
      const accts = aR.data?.accounts || (Array.isArray(aR.data) ? aR.data : []);
      setAccounts(accts);
      setHopQuotes({});
      const loaded = cR.data?.chains || [];
      setChains(loaded);
      // Prune selections that no longer exist after refilter/refetch
      setSelected(prev => new Set([...prev].filter(id => loaded.some(c => c.id === id))));
      setTotal(cR.data?.total || 0);
      setTotalPL(cR.data?.total_pl || 0);
      prefetchOpenMarks(loaded, accts);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load chains');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, accountFilter, page, rowsPerPage, search, sortBy, sortDir, range.from, range.to]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onSort = col => {
    setPage(0);
    if (sortBy === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const sortable = (col, label, align) => (
    <TableCell align={align} sortDirection={sortBy === col ? sortDir : false}>
      <TableSortLabel active={sortBy === col} direction={sortBy === col ? sortDir : 'asc'} onClick={() => onSort(col)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const rebuild = async () => {
    setRebuilding(true);
    try {
      await apiRequest('/api/trade-chains/rebuild', { method: 'POST', data: {} });
      await fetchAll();
    } catch (e) {
      setError(e.response?.data?.error || 'Rebuild failed');
    } finally {
      setRebuilding(false);
    }
  };

  const unlink = async (hop) => {
    try {
      await apiRequest('/api/trade-chains/unlink', { method: 'POST', body: JSON.stringify({ trade_ids: hop.trade_ids }) });
      await fetchAll();
    } catch (e) {
      setError(e.response?.data?.error || 'Unlink failed');
    }
  };

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allPageSelected = chains.length > 0 && chains.every(c => selected.has(c.id));
  const toggleSelectAll = () => setSelected(prev => {
    const next = new Set(prev);
    if (allPageSelected) chains.forEach(c => next.delete(c.id));
    else chains.forEach(c => next.add(c.id));
    return next;
  });

  // Merge selected chains into the earliest-opened one — rolls extend the
  // oldest lifecycle. Each non-target chain's hops move wholesale via /link.
  const mergeSelected = async () => {
    if (selected.size < 2) return;
    const picks = chains.filter(c => selected.has(c.id));
    const target = picks.reduce((a, b) => (new Date(a.opened_at) <= new Date(b.opened_at) ? a : b));
    setMerging(true);
    try {
      for (const ch of picks.filter(c => c.id !== target.id)) {
        const ids = (ch.hops || []).flatMap(h => h.trade_ids || []);
        if (ids.length) {
          await apiRequest('/api/trade-chains/link', {
            method: 'POST',
            body: JSON.stringify({ trade_ids: ids, chain_id: target.id }),
          });
        }
      }
      setSelected(new Set());
      await fetchAll();
    } catch (e) {
      setError(e.response?.data?.error || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const doLink = async () => {
    try {
      await apiRequest('/api/trade-chains/link', {
        method: 'POST',
        body: JSON.stringify({ trade_ids: linkHop.trade_ids, chain_id: +linkTarget }),
      });
      setLinkHop(null);
      setLinkTarget('');
      await fetchAll();
    } catch (e) {
      setError(e.response?.data?.error || 'Link failed');
    }
  };

  // Descriptive contract labels ("VST 10/23/26 $145 Put") aren't quotable —
  // rebuild the OCC/OSI form the quote APIs accept. Stocks pass through.
  const quoteSymbolFor = (contract) => {
    const info = parseOptionSymbol(contract);
    if (info?.isOption && !info.isFuturesOption && info.expiration && info.type && info.strike != null) {
      const e = info.expiration; // YYMMDD
      const iso = `20${e.slice(0, 2)}-${e.slice(2, 4)}-${e.slice(4, 6)}`;
      return buildOCCSymbol(info.underlying, iso, info.type, info.strike);
    }
    return contract;
  };
  const quoteKeys = (contract) => [
    String(contract).replace(/\s+/g, ''),
    String(quoteSymbolFor(contract)).replace(/\s+/g, ''),
  ];

  // Fetch live marks for open-hop contracts so Hop P/L and the chain-level
  // P/L can include unrealized value instead of sitting at $0.
  const fetchQuotes = async (accountId, symbols, accts) => {
    const needed = [...new Set(symbols)].filter(s =>
      !hopQuotes[s.replace(/\s+/g, '')] && !pendingQuotes.current.has(s));
    if (!needed.length) return;
    needed.forEach(s => pendingQuotes.current.add(s));
    try {
      const acc = (accts || accounts).find(a => String(a.id) === String(accountId));
      const apiType = String(acc?.apiType || acc?.api_type || '').toLowerCase();
      let map = {};
      if (apiType === 'schwab') {
        const r = await apiRequest('/api/schwab-api/quotes', {
          method: 'POST',
          body: JSON.stringify({ accountId, symbols: needed }),
        });
        map = r.data?.quotes || {};
      } else {
        const r = await apiRequest(`/api/accounts/${accountId}/quotes`, {
          method: 'POST',
          body: JSON.stringify({ symbols: needed }),
        });
        const qData = r.data?.quotes?.quote || r.data?.quotes?.quotes?.quote || [];
        (Array.isArray(qData) ? qData : [qData]).forEach(q => {
          if (!q?.symbol) return;
          const key = String(q.symbol).replace(/\s+/g, '');
          const bid = parseFloat(q.bid) || 0;
          const ask = parseFloat(q.ask) || 0;
          map[key] = { bid, ask, mid: bid && ask ? (bid + ask) / 2 : (bid || ask || 0), last: parseFloat(q.last) || 0 };
        });
      }
      if (Object.keys(map).length) setHopQuotes(prev => ({ ...prev, ...map }));
    } catch {
      // Quotes are best-effort — a missing mark just leaves the hop at realized P/L.
    } finally {
      needed.forEach(s => pendingQuotes.current.delete(s));
    }
  };

  const openHopSymbols = (ch) => {
    const syms = new Set();
    for (const h of ch.hops || []) {
      if (!h.open) continue;
      for (const c of h.contracts || []) {
        if (c.qty !== 0 && c.contract) syms.add(quoteSymbolFor(c.contract));
      }
    }
    return [...syms];
  };

  const fetchHopQuotes = (ch) => fetchQuotes(ch.account_id, openHopSymbols(ch));

  // Prefetch marks for every open hop on the loaded page — grouped per
  // account so each broker API is hit once.
  const prefetchOpenMarks = (list, accts) => {
    const byAcct = new Map();
    for (const ch of list || []) {
      const syms = openHopSymbols(ch);
      if (!syms.length) continue;
      if (!byAcct.has(ch.account_id)) byAcct.set(ch.account_id, new Set());
      syms.forEach(s => byAcct.get(ch.account_id).add(s));
    }
    for (const [acctId, syms] of byAcct) fetchQuotes(acctId, [...syms], accts);
  };

  const toggle = id => {
    const ch = chains.find(c => c.id === id);
    if (ch && !expanded.has(id)) fetchHopQuotes(ch);
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  // Current P/L for a hop: booked realized P/L on closed fills plus the
  // unrealized mark of any contracts still open. For partially closed hops
  // the opening premium is prorated to the remaining quantity.
  const hopCurrentPL = (h) => {
    let unrealized = 0;
    let marked = false;
    for (const c of h.contracts || []) {
      if (c.qty === 0) continue;
      const q = quoteKeys(c.contract).map(k => hopQuotes[k]).find(Boolean);
      if (!q) continue;
      const mark = q.mid || q.mark || q.last || q.bid || q.ask || 0;
      if (!mark) continue;
      marked = true;
      const openQty = c.openQty || c.qty;
      const remainingPremium = openQty ? (c.premium || 0) * (c.qty / openQty) : 0;
      const mult = c.isStock ? 1 : 100;
      unrealized += remainingPremium + c.qty * mark * mult;
    }
    return { pl: (h.realized_pl || 0) + unrealized, hasOpenMark: marked };
  };

  // Chain P/L = booked P/L of closed hops plus the live-marked value of open
  // hops. Closed hops with no booked realized_pl fall back to fill cash flow;
  // open hops without a mark show realized only (their premium is cost basis,
  // not P/L).
  const hopPL = (h) => {
    const cur = hopCurrentPL(h);
    if (h.open) return { pl: cur.hasOpenMark ? cur.pl : (h.realized_pl || 0), marked: cur.hasOpenMark };
    return { pl: h.realized_pl || h.flow_pl || 0, marked: false };
  };
  const chainPL = (ch) => {
    if (!ch.hops?.length) return { pl: ch.realized_pl || 0, marked: false };
    let pl = 0, marked = false;
    for (const h of ch.hops) {
      const hp = hopPL(h);
      pl += hp.pl;
      marked = marked || hp.marked;
    }
    return { pl, marked };
  };
  // Header total = server-side realized across ALL chains plus the live-mark
  // delta of the chains on this page (the only ones we have marks for).
  const loadedDelta = chains.reduce(
    (s, ch) => s + chainPL(ch).pl - (ch.realized_pl || 0), 0);
  const displayTotal = totalPL + loadedDelta;

  const chainLabel = ch => {
    const first = ch.hops?.[0]?.label || ch.underlying;
    return `${ch.underlying} — ${first}`;
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <LinkIcon color="primary" />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Chains</Typography>
            <TextField
              size="small" placeholder="Underlying… (e.g. MU -MUB -TMUS)" value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              sx={{ minWidth: 160 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Duration</InputLabel>
              <Select value={duration} label="Duration" onChange={e => { setDuration(e.target.value); setPage(0); }}>
                <MenuItem value="">All time</MenuItem>
                <MenuItem value="ytd">YTD</MenuItem>
                <MenuItem value="30">Last 30 days</MenuItem>
                <MenuItem value="90">Last 90 days</MenuItem>
                <MenuItem value="180">Last 6 months</MenuItem>
                <MenuItem value="365">Last year</MenuItem>
                <MenuItem value="custom">Custom…</MenuItem>
              </Select>
            </FormControl>
            {duration === 'custom' && (
              <>
                <TextField size="small" type="date" label="From" value={customFrom}
                  onChange={e => { setCustomFrom(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
                <TextField size="small" type="date" label="To" value={customTo}
                  onChange={e => { setCustomTo(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
              </>
            )}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Account</InputLabel>
              <Select value={accountFilter} label="Account" onChange={e => { setAccountFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                {accounts.map(a => (
                  <MenuItem key={a.id} value={String(a.id)}>{a.name || `#${a.id}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={e => { setStatusFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
            {selected.size >= 2 && (
              <Button size="small" variant="contained" color="primary"
                startIcon={merging ? <CircularProgress size={14} /> : <LinkIcon />}
                onClick={mergeSelected} disabled={merging}
                title="Merge selected chains into the earliest-opened one">
                Chain {selected.size} selected
              </Button>
            )}
            <Button size="small" variant="outlined" startIcon={rebuilding ? <CircularProgress size={14} /> : <SyncIcon />}
              onClick={rebuild} disabled={rebuilding}>
              Recompute
            </Button>
            <IconButton size="small" onClick={fetchAll}><Refresh /></IconButton>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : chains.length === 0 ? (
            <Alert severity="info">
              No chains yet. Run <b>Recompute</b> to build them from your trade history —
              rolls, assignments, and covered calls on the same underlying get linked automatically.
            </Alert>
          ) : (
            <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {total.toLocaleString()} chains
              </Typography>
              <Tooltip title={loadedDelta ? 'Includes live-marked value of open hops on this page' : 'Realized P/L across all chains'} arrow>
                <Typography variant="body2" fontWeight={700} color={plColor(displayTotal)}>
                  Total P/L {fmt$(displayTotal)}{loadedDelta ? '*' : ''}
                </Typography>
              </Tooltip>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40} padding="checkbox">
                      <Checkbox size="small" checked={allPageSelected}
                        indeterminate={selected.size > 0 && !allPageSelected}
                        onChange={toggleSelectAll}
                        title="Select all on this page" />
                    </TableCell>
                    <TableCell width={40} />
                    {sortable('underlying', 'Chain')}
                    {sortable('account', 'Account')}
                    {sortable('hops', 'Hops', 'center')}
                    {sortable('opened', 'Opened')}
                    {sortable('closed', 'Closed')}
                    {sortable('status', 'Status')}
                    {sortable('pl', 'P/L', 'right')}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chains.map(ch => (
                    <React.Fragment key={ch.id}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggle(ch.id)} selected={selected.has(ch.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={selected.has(ch.id)}
                            onChange={() => toggleSelect(ch.id)}
                            onClick={e => e.stopPropagation()} />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small">
                            {expanded.has(ch.id) ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{chainLabel(ch)}</Typography>
                          {ch.manual && <Chip label="manual" size="small" variant="outlined" sx={{ ml: 1, height: 18 }} />}
                        </TableCell>
                        <TableCell>{accountName(ch.account_id)}</TableCell>
                        <TableCell align="center">{ch.hop_count}</TableCell>
                        <TableCell>{fmtDate(ch.opened_at)}</TableCell>
                        <TableCell>{fmtDate(ch.closed_at)}</TableCell>
                        <TableCell>
                          <Chip label={ch.status} size="small"
                            color={ch.status === 'open' ? 'primary' : 'default'} variant={ch.status === 'open' ? 'filled' : 'outlined'} />
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            const cp = chainPL(ch);
                            return (
                              <Typography variant="body2" fontWeight={700} color={plColor(cp.pl)}
                                title={cp.marked ? 'Includes live mark of open hops' : undefined}>
                                {fmt$(cp.pl)}{cp.marked ? '*' : ''}
                              </Typography>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={9} sx={{ p: 0, borderBottom: expanded.has(ch.id) ? undefined : 'none' }}>
                          <Collapse in={expanded.has(ch.id)} unmountOnExit>
                            <Box sx={{ pl: 6, pr: 2, py: 1, bgcolor: 'action.hover' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Hop</TableCell>
                                    <TableCell>Position</TableCell>
                                    <TableCell align="right">Premium</TableCell>
                                    <TableCell>Opened</TableCell>
                                    <TableCell>Closed</TableCell>
                                    <TableCell align="right">Hop P/L</TableCell>
                                    <TableCell align="right">Running P/L</TableCell>
                                    <TableCell align="right" width={90} />
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(() => {
                                    // Running P/L accumulates chronologically (hop order);
                                    // display puts open hops on top, newest opened first.
                                    const runningByKey = new Map();
                                    let running = 0;
                                    [...ch.hops].sort((a, b) => a.hop - b.hop).forEach(h => {
                                      running += hopPL(h).pl;
                                      runningByKey.set(h.hop, running);
                                    });
                                    const disp = [...ch.hops].sort((a, b) =>
                                      !!a.open === !!b.open
                                        ? new Date(b.open_ts) - new Date(a.open_ts)
                                        : (a.open ? -1 : 1));
                                    return disp.map(h => {
                                      const hp = hopPL(h);
                                      const hopKey = `${ch.id}:${h.hop}`;
                                      const hopOpen = expandedHops.has(hopKey);
                                      const hopFills = (h.contracts || []).flatMap(c =>
                                        (c.fills || []).map(f => ({ ...f, contract: c.contract })));
                                      return (
                                        <React.Fragment key={h.hop}>
                                        <TableRow
                                          hover sx={{ cursor: 'pointer' }}
                                          onClick={() => setExpandedHops(prev => {
                                            const next = new Set(prev);
                                            next.has(hopKey) ? next.delete(hopKey) : next.add(hopKey);
                                            return next;
                                          })}
                                        >
                                          <TableCell>{h.hop}</TableCell>
                                          <TableCell>
                                            {hopOpen ? <KeyboardArrowDown sx={{ fontSize: 14, verticalAlign: 'middle' }} /> : <KeyboardArrowRight sx={{ fontSize: 14, verticalAlign: 'middle' }} />}
                                            {h.label}
                                            {h.assigned && <Chip label="assigned" size="small" color="warning" sx={{ ml: 1, height: 18 }} />}
                                            {h.expired && !h.open && <Chip label="expired" size="small" color="default" variant="outlined" sx={{ ml: 1, height: 18 }} />}
                                            {h.open && <Chip label="open" size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 18 }} />}
                                          </TableCell>
                                          <TableCell align="right">
                                            {h.premium != null && h.premium !== 0 && (
                                              <Typography variant="body2" color={h.premium > 0 ? 'success.main' : 'error.main'}>
                                                {h.premium > 0 ? 'Cr' : 'Dr'} {fmt$(Math.abs(h.premium))}
                                              </Typography>
                                            )}
                                          </TableCell>
                                          <TableCell>{fmtDate(h.open_ts)}</TableCell>
                                          <TableCell>{fmtDate(h.close_ts)}</TableCell>
                                          <TableCell align="right">
                                            {h.open && hp.marked ? (
                                              <Tooltip title="Open hop — includes live mark of remaining contracts" arrow>
                                                <Typography variant="body2" color={plColor(hp.pl)}>{fmt$(hp.pl)}*</Typography>
                                              </Tooltip>
                                            ) : (
                                              <Typography variant="body2" color={plColor(hp.pl)}>{fmt$(hp.pl)}</Typography>
                                            )}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2" fontWeight={600} color={plColor(runningByKey.get(h.hop))}>{fmt$(runningByKey.get(h.hop))}</Typography>
                                          </TableCell>
                                          <TableCell align="right" onClick={e => e.stopPropagation()}>
                                            <Tooltip title="Link to another chain">
                                              <IconButton size="small" onClick={() => { setLinkHop({ trade_ids: h.trade_ids, fromChainId: ch.id, label: h.label }); setLinkTarget(''); }}>
                                                <LinkIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Unlink into its own chain">
                                              <IconButton size="small" onClick={() => unlink(h)}>
                                                <LinkOffIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>
                                        {hopOpen && (
                                          <TableRow>
                                            <TableCell colSpan={8} sx={{ p: 0, bgcolor: 'background.paper' }}>
                                              <Box sx={{ pl: 8, pr: 2, py: 0.5 }}>
                                                <Table size="small">
                                                  <TableHead>
                                                    <TableRow>
                                                      <TableCell>Date</TableCell>
                                                      <TableCell>Type</TableCell>
                                                      <TableCell>Contract</TableCell>
                                                      <TableCell align="right">Qty</TableCell>
                                                      <TableCell align="right">Price</TableCell>
                                                      <TableCell align="right">P/L</TableCell>
                                                    </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    {hopFills.length === 0 ? (
                                                      <TableRow><TableCell colSpan={6}><Typography variant="caption" color="text.secondary">No fills</Typography></TableCell></TableRow>
                                                    ) : hopFills.map(f => (
                                                      <TableRow key={f.id}>
                                                        <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{fmtDate(f.date)}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.75rem' }}>{f.type}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.75rem' }}>{f.contract}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{f.qty}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{fmt$(f.price)}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                                          <Typography variant="body2" component="span" color={plColor(f.pl)} sx={{ fontSize: '0.75rem' }}>
                                                            {f.pl != null ? fmt$(f.pl) : '—'}
                                                          </Typography>
                                                        </TableCell>
                                                      </TableRow>
                                                    ))}
                                                  </TableBody>
                                                </Table>
                                              </Box>
                                            </TableCell>
                                          </TableRow>
                                        )}
                                        </React.Fragment>
                                      );
                                    });
                                  })()}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
          {!loading && chains.length > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={!!linkHop} onClose={() => setLinkHop(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Link hop into chain</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{linkHop?.label}</Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Target chain</InputLabel>
            <Select value={linkTarget} label="Target chain" onChange={e => setLinkTarget(e.target.value)}>
              {chains.filter(c => c.id !== linkHop?.fromChainId).map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {chainLabel(c)} · {accountName(c.account_id)} · {c.status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkHop(null)}>Cancel</Button>
          <Button variant="contained" onClick={doLink} disabled={!linkTarget}>Link</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Chains;
